import React, { useState } from 'react';
import { Steps, Form, Select, Input, InputNumber, Button, Collapse, Space, Spin } from 'antd';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import AvatarUpload from './AvatarUpload';
import EmojiTextArea from './EmojiTextArea';
import IdentityLegalStep, { extractCompleteBillingIdentity } from './IdentityLegalStep';
import { useUserProfile } from '../contextProviders/UserProfileProvider';
import { useServiceProviderProfile } from '../contextProviders/ServiceProviderProfileProvider';
import { createHostProfile } from '../services/HostProfileService';
import { uploadPhotos } from '../services/MediaService';
import { ICreateHostProfileRequest, IHostProfile, HostAccountType } from '../interfaces/heavyDutyMachinery/IHostProfile';
import { HOST_BUSINESS_CATEGORY_OPTIONS, HOST_LANGUAGE_OPTIONS } from '../constants/hostProfileOptions';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface HostOnboardingWizardProps {
  onComplete: (profile: IHostProfile) => void;
  onCancel?: () => void;
}

interface WizardFormValues {
  accountType?: HostAccountType;
  businessCategory?: string;
  businessCategoryOther?: string;
  legalName?: string;
  taxId?: string;
  businessRegistrationNumber?: string;
  contactPhone?: string;
  billingAddress?: string;
  insurancePolicyNumber?: string;
  profilePhotoOrLogo?: string;
  displayName?: string;
  tagline?: string;
  languagesSpoken?: string[];
  personalBio?: string;
  companyDescription?: string;
  foundedYear?: number;
  teamSize?: string;
  certifications?: string[];
  brandStory?: string;
}

/**
 * First-run wizard for HostProfile creation -- triggered the first time a
 * user without one tries to publish a HeavyDutyMachinery (see HeavyDutyMachineryForm
 * and MyHeavyDutyMachineries). A single Form instance spans every step so values
 * survive Back/Next; only the final step actually posts anything
 * (CreateHostProfile), never a partial profile mid-wizard.
 */
const HostOnboardingWizard: React.FC<HostOnboardingWizardProps> = ({ onComplete, onCancel }) => {
  const { t } = useTranslation();
  const { userProfile } = useUserProfile();
  const accountDisplayName = userProfile?.displayName;
  // A complete UserBillingProfile already on file via this same person's
  // ServiceProviderProfile (if any) -- see IdentityLegalStep's own doc
  // comment on existingBilling for why this makes that step a read-only
  // summary instead of the usual form.
  const { serviceProviderProfile, loading: serviceProviderProfileLoading } = useServiceProviderProfile();
  const existingBilling = extractCompleteBillingIdentity(serviceProviderProfile);
  const [form] = Form.useForm<WizardFormValues>();
  // Tracked by step KEY rather than a numeric index into stepKeys -- stepKeys
  // itself changes shape depending on isBusiness (3 vs 4 entries), so an
  // index can silently point at the wrong step the moment that shape
  // changes. A key is unambiguous regardless of how stepKeys is computed.
  const [stepKey, setStepKey] = useState<string>('identity');
  const [submitting, setSubmitting] = useState(false);
  // accountType/businessCategory are tracked via onValuesChange rather than
  // Form.useWatch -- both drive which steps exist and how many there are
  // (stepKeys), and their own Form.Item unmounts as soon as the wizard
  // leaves that step, which made Form.useWatch unreliable here (it stopped
  // reflecting the real value once nothing was mounted to represent it,
  // causing stepKeys.indexOf(stepKey) to come back -1 and navigation to
  // reset to step 0). onValuesChange fires synchronously off the Form
  // instance itself regardless of which Form.Items are currently mounted.
  const [accountType, setAccountType] = useState<HostAccountType>('Individual');
  const [businessCategory, setBusinessCategory] = useState<string | undefined>(undefined);
  const photoUrl = Form.useWatch('profilePhotoOrLogo', form);

  const isBusiness = accountType === 'Business';

  const handleValuesChange = (changedValues: Partial<WizardFormValues>) => {
    if ('accountType' in changedValues) setAccountType(changedValues.accountType ?? 'Individual');
    if ('businessCategory' in changedValues) setBusinessCategory(changedValues.businessCategory);
  };

  // existingBilling's AccountType can never be re-asked (see
  // IdentityLegalStep's own doc comment) -- once it resolves, this wizard's
  // OWN isBusiness branching (businessCategory step, public step's field
  // set) must follow it from the start rather than default to Individual.
  // A plain lazy useState initializer can't do this: existingBilling is
  // still undefined on the very first render, before
  // ServiceProviderProfileProvider's own fetch resolves.
  React.useEffect(() => {
    if (existingBilling) setAccountType(existingBilling.accountType);
  }, [existingBilling]);

  // Precarga el nombre público del host a partir de la cuenta personal (Mi
  // cuenta) como punto de partida editable -- evita pedir de nuevo un dato
  // que ya existe, sin fusionar los campos: siguen siendo columnas
  // independientes en HostProfile, solo el valor inicial coincide. Solo
  // rellena si el campo del wizard sigue vacío, para no pisar lo que el
  // usuario ya haya escrito si accountDisplayName llega (o cambia) más
  // tarde de forma asíncrona.
  //
  // ProfilePhotoOrLogo NUNCA se precarga con la foto de la cuenta -- son dos
  // fotos completamente independientes (foto personal vs. foto/logo del
  // espacio), y el host siempre debe partir de un campo vacío que invite a
  // subir una nueva, sea cuenta Individual o Business.
  React.useEffect(() => {
    if (accountDisplayName && !form.getFieldValue('displayName')) {
      form.setFieldsValue({ displayName: accountDisplayName });
    }
  }, [accountDisplayName, form]);

  // Individual accounts skip the "business category" step entirely.
  const stepKeys = isBusiness ? ['identity', 'businessCategory', 'legal', 'public'] : ['identity', 'legal', 'public'];
  const currentIndex = stepKeys.indexOf(stepKey);

  const fieldsForStep: Record<string, (keyof WizardFormValues)[]> = {
    // Nothing to validate once existingBilling already reuses the other
    // role's row -- IdentityLegalStep doesn't render these Form.Items at
    // all in that case (see its own doc comment on existingBilling).
    identity: existingBilling ? [] : ['accountType', 'legalName', 'taxId', 'contactPhone', 'billingAddress'],
    businessCategory: businessCategory === 'Other' ? ['businessCategory', 'businessCategoryOther'] : ['businessCategory'],
    // BusinessCategory/businessRegistrationNumber/insurancePolicyNumber stay
    // host-specific, never shared -- legalName/taxId/contactPhone/
    // billingAddress moved to IdentityLegalStep above.
    legal: isBusiness ? ['businessRegistrationNumber', 'insurancePolicyNumber'] : ['insurancePolicyNumber'],
    public: isBusiness
      ? ['displayName', 'tagline', 'languagesSpoken', 'companyDescription']
      : ['displayName', 'tagline', 'languagesSpoken', 'personalBio'],
  };

  const goNext = async () => {
    try {
      await form.validateFields(fieldsForStep[stepKey]);
      const nextKey = stepKeys[currentIndex + 1];
      if (nextKey) setStepKey(nextKey);
    } catch {
      // antd already highlights the invalid fields.
    }
  };

  const goBack = () => {
    const previousKey = stepKeys[currentIndex - 1];
    if (previousKey) setStepKey(previousKey);
  };

  const handleFinish = async () => {
    try {
      await form.validateFields(fieldsForStep[stepKey]);
    } catch {
      return;
    }

    const values = form.getFieldsValue(true);
    const request: ICreateHostProfileRequest = {
      accountType: values.accountType!,
      businessCategory: isBusiness ? (values.businessCategory as any) : undefined,
      businessCategoryOther: isBusiness && values.businessCategory === 'Other' ? values.businessCategoryOther : undefined,
      legalName: values.legalName!,
      taxId: values.taxId,
      businessRegistrationNumber: isBusiness ? values.businessRegistrationNumber : undefined,
      contactPhone: values.contactPhone!,
      billingAddress: values.billingAddress!,
      insurancePolicyNumber: values.insurancePolicyNumber,
      displayName: values.displayName!,
      profilePhotoOrLogo: values.profilePhotoOrLogo,
      tagline: values.tagline,
      languagesSpoken: values.languagesSpoken?.join(',') || undefined,
      personalBio: !isBusiness ? values.personalBio : undefined,
      companyDescription: isBusiness ? values.companyDescription : undefined,
      foundedYear: isBusiness ? values.foundedYear : undefined,
      teamSize: isBusiness ? values.teamSize : undefined,
      certifications: isBusiness ? values.certifications?.join(',') || undefined : undefined,
      brandStory: isBusiness ? values.brandStory : undefined,
    };

    setSubmitting(true);
    try {
      const profile = await createHostProfile(request);
      message.success(translate(t, TRANSLATIONS.HOST_ONBOARDING_SUCCESS));
      onComplete(profile);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HOST_ONBOARDING_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const isLastStep = currentIndex === stepKeys.length - 1;

  // Blocks on the OTHER role's own profile fetch before deciding whether
  // the identity step opens as a blank form or a read-only summary -- same
  // "gate on loading before rendering" convention HostOnboardingPage/
  // ServiceProviderOnboardingPage already use for their own profile.
  if (serviceProviderProfileLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
    <div>
      <Steps
        current={currentIndex}
        size="small"
        style={{ marginBottom: 28 }}
        items={stepKeys.map((key) => ({ title: translate(t, STEP_TITLE_KEYS[key]) }))}
      />

      <Form form={form} layout="vertical" initialValues={{ accountType: 'Individual' }} onValuesChange={handleValuesChange}>
        {stepKey === 'identity' && (
          <IdentityLegalStep form={form} submitting={submitting} accountType={accountType} existingBilling={existingBilling} />
        )}

        {stepKey === 'businessCategory' && (
          <Form.Item
            name="businessCategory"
            label={translate(t, TRANSLATIONS.HOST_ONBOARDING_BUSINESS_CATEGORY_QUESTION)}
            rules={[{ required: true }]}
          >
            <Select
              options={HOST_BUSINESS_CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))}
              placeholder={translate(t, TRANSLATIONS.HOST_ONBOARDING_BUSINESS_CATEGORY_PLACEHOLDER)}
            />
          </Form.Item>
        )}

        {stepKey === 'businessCategory' && businessCategory === 'Other' && (
          <Form.Item
            name="businessCategoryOther"
            label={translate(t, TRANSLATIONS.HOST_FIELD_BUSINESS_CATEGORY_OTHER_LABEL)}
            rules={[{ required: true }]}
          >
            <Input disabled={submitting} />
          </Form.Item>
        )}

        {stepKey === 'legal' && (
          <>
            {isBusiness && (
              <Form.Item
                name="businessRegistrationNumber"
                label={translate(t, TRANSLATIONS.HOST_FIELD_BUSINESS_REGISTRATION_NUMBER)}
                rules={[{ required: true }]}
              >
                <Input disabled={submitting} />
              </Form.Item>
            )}
            <Form.Item
              name="insurancePolicyNumber"
              label={translate(t, TRANSLATIONS.HOST_FIELD_INSURANCE_POLICY_NUMBER)}
              rules={[{ required: isBusiness }]}
              extra={!isBusiness ? translate(t, TRANSLATIONS.HOST_FIELD_INSURANCE_POLICY_NUMBER_HINT) : undefined}
            >
              <Input disabled={submitting} />
            </Form.Item>
          </>
        )}

        {stepKey === 'public' && (
          <>
            <Form.Item label={translate(t, TRANSLATIONS.HOST_FIELD_PROFILE_PHOTO)}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <AvatarUpload
                  value={photoUrl}
                  onChange={(url) => form.setFieldsValue({ profilePhotoOrLogo: url })}
                  uploadFn={(file) => uploadPhotos('hosts', [file]).then((uploaded) => uploaded[0].url)}
                  disabled={submitting}
                />
              </div>
            </Form.Item>
            <Form.Item name="profilePhotoOrLogo" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              name="displayName"
              label={translate(t, TRANSLATIONS.HOST_FIELD_PUBLIC_HOST_NAME)}
              extra={translate(t, TRANSLATIONS.HOST_FIELD_PUBLIC_HOST_NAME_HINT)}
              rules={[{ required: true }]}
            >
              <Input disabled={submitting} placeholder={translate(t, TRANSLATIONS.HOST_FIELD_PUBLIC_HOST_NAME_PLACEHOLDER)} />
            </Form.Item>
            <Form.Item name="tagline" label={translate(t, TRANSLATIONS.HOST_FIELD_TAGLINE)}>
              <Input maxLength={100} showCount disabled={submitting} />
            </Form.Item>
            <Form.Item name="languagesSpoken" label={translate(t, TRANSLATIONS.HOST_FIELD_LANGUAGES_SPOKEN)}>
              <Select
                mode="multiple"
                options={HOST_LANGUAGE_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))}
                disabled={submitting}
              />
            </Form.Item>

            {!isBusiness && (
              <Form.Item name="personalBio" label={translate(t, TRANSLATIONS.HOST_FIELD_PERSONAL_BIO)} rules={[{ required: true }]}>
                <EmojiTextArea rows={4} maxLength={2000} disabled={submitting} />
              </Form.Item>
            )}

            {isBusiness && (
              <>
                <Form.Item name="companyDescription" label={translate(t, TRANSLATIONS.HOST_FIELD_COMPANY_DESCRIPTION)} rules={[{ required: true }]}>
                  <EmojiTextArea rows={4} maxLength={2000} disabled={submitting} />
                </Form.Item>
                <Space size="large" style={{ display: 'flex' }}>
                  <Form.Item name="foundedYear" label={translate(t, TRANSLATIONS.HOST_FIELD_FOUNDED_YEAR)} style={{ flex: 1 }}>
                    <InputNumber min={1800} max={new Date().getFullYear()} style={{ width: '100%' }} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="teamSize" label={translate(t, TRANSLATIONS.HOST_FIELD_TEAM_SIZE)} style={{ flex: 1 }}>
                    <Input placeholder={translate(t, TRANSLATIONS.HOST_FIELD_TEAM_SIZE_PLACEHOLDER)} disabled={submitting} />
                  </Form.Item>
                </Space>
                <Form.Item name="certifications" label={translate(t, TRANSLATIONS.HOST_FIELD_CERTIFICATIONS)}>
                  <Select mode="tags" open={false} disabled={submitting} placeholder={translate(t, TRANSLATIONS.HOST_FIELD_CERTIFICATIONS_PLACEHOLDER)} />
                </Form.Item>
                <Collapse
                  ghost
                  items={[
                    {
                      key: 'brandStory',
                      label: translate(t, TRANSLATIONS.HOST_FIELD_BRAND_STORY_TOGGLE),
                      children: (
                        <Form.Item name="brandStory" label={translate(t, TRANSLATIONS.HOST_FIELD_BRAND_STORY)}>
                          <EmojiTextArea rows={4} maxLength={4000} disabled={submitting} />
                        </Form.Item>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Form>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
        <div>
          {currentIndex > 0 && (
            <Button shape="round" onClick={goBack} disabled={submitting}>
              {translate(t, TRANSLATIONS.HOST_ONBOARDING_BACK)}
            </Button>
          )}
          {currentIndex === 0 && onCancel && (
            <Button shape="round" onClick={onCancel} disabled={submitting}>
              {translate(t, TRANSLATIONS.HOST_ONBOARDING_CANCEL)}
            </Button>
          )}
        </div>
        {isLastStep ? (
          <Button type="primary" shape="round" onClick={handleFinish} loading={submitting}>
            {translate(t, TRANSLATIONS.HOST_ONBOARDING_FINISH)}
          </Button>
        ) : (
          <Button type="primary" shape="round" onClick={goNext}>
            {translate(t, TRANSLATIONS.HOST_ONBOARDING_NEXT)}
          </Button>
        )}
      </div>
    </div>
    </>
  );
};

const STEP_TITLE_KEYS: Record<string, string> = {
  identity: TRANSLATIONS.HOST_ONBOARDING_STEP_LEGAL,
  businessCategory: TRANSLATIONS.HOST_ONBOARDING_STEP_BUSINESS_CATEGORY,
  // What's left of the old 'legal' step once legalName/taxId/contactPhone/
  // billingAddress moved to the 'identity' step above -- businessRegistrationNumber/
  // insurancePolicyNumber are specific to the hosting business, not the
  // person's fiscal identity (see HostProfile's own doc comment).
  legal: TRANSLATIONS.HOST_ONBOARDING_STEP_BUSINESS_DETAILS,
  public: TRANSLATIONS.HOST_ONBOARDING_STEP_PUBLIC,
};

export default HostOnboardingWizard;
